import Cookies from 'js-cookie';

// Cookie names
export const COOKIE_NAMES = {
    ANALYTICS_CONSENT: 'analytics_consent',
    FUNCTIONAL_CONSENT: 'functional_consent',
    MARKETING_CONSENT: 'marketing_consent',
    PREFERENCES: 'user_preferences',
    SESSION_ID: 'session_id'
} as const;

// Cookie options
const COOKIE_OPTIONS = {
    expires: 365, // 1 year
    secure: window.location.protocol === 'https:',
    sameSite: 'lax' as const,
    domain: window.location.hostname.includes('localhost') ? undefined : window.location.hostname
};

// Get cookie value
export const getCookie = (name: string): string | undefined => {
    return Cookies.get(name);
};

// Set cookie value
export const setCookie = (name: string, value: string, options?: Cookies.CookieAttributes): void => {
    Cookies.set(name, value, { ...COOKIE_OPTIONS, ...options });
};

// Remove cookie
export const removeCookie = (name: string): void => {
    Cookies.remove(name, {
        domain: COOKIE_OPTIONS.domain,
        path: '/'
    });
};

// Check if user has given analytics consent
export const hasAnalyticsConsent = (): boolean => {
    return getCookie(COOKIE_NAMES.ANALYTICS_CONSENT) === 'true';
};

// Set analytics consent
export const setAnalyticsConsent = (consent: boolean): void => {
    setCookie(COOKIE_NAMES.ANALYTICS_CONSENT, consent.toString());
};

// Check if user has given functional consent
export const hasFunctionalConsent = (): boolean => {
    return getCookie(COOKIE_NAMES.FUNCTIONAL_CONSENT) === 'true';
};

// Set functional consent
export const setFunctionalConsent = (consent: boolean): void => {
    setCookie(COOKIE_NAMES.FUNCTIONAL_CONSENT, consent.toString());
};

// Check if user has given marketing consent
export const hasMarketingConsent = (): boolean => {
    return getCookie(COOKIE_NAMES.MARKETING_CONSENT) === 'true';
};

// Set marketing consent
export const setMarketingConsent = (consent: boolean): void => {
    setCookie(COOKIE_NAMES.MARKETING_CONSENT, consent.toString());
};

// Get all consent status
export const getConsentStatus = () => {
    return {
        analytics: hasAnalyticsConsent(),
        functional: hasFunctionalConsent(),
        marketing: hasMarketingConsent()
    };
};

// Set all consent
export const setAllConsent = (consents: {
    analytics: boolean;
    functional: boolean;
    marketing: boolean;
}): void => {
    setAnalyticsConsent(consents.analytics);
    setFunctionalConsent(consents.functional);
    setMarketingConsent(consents.marketing);
};

// Clear all consent cookies
export const clearAllConsent = (): void => {
    removeCookie(COOKIE_NAMES.ANALYTICS_CONSENT);
    removeCookie(COOKIE_NAMES.FUNCTIONAL_CONSENT);
    removeCookie(COOKIE_NAMES.MARKETING_CONSENT);
};

// Generate and store session ID
export const generateSessionId = (): string => {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('session_id', sessionId);
    return sessionId;
};

// Get or create session ID
export const getSessionId = (): string => {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
        sessionId = generateSessionId();
    }
    return sessionId;
};

// Store user preferences
export const setUserPreferences = (preferences: Record<string, any>): void => {
    setCookie(COOKIE_NAMES.PREFERENCES, JSON.stringify(preferences));
};

// Get user preferences
export const getUserPreferences = (): Record<string, any> => {
    const prefs = getCookie(COOKIE_NAMES.PREFERENCES);
    return prefs ? JSON.parse(prefs) : {};
}; 